<template>
  <ElDialog
    name="add-payment-method-modal"
    class="modal-add-payment"
    :visible="show"
    :title=title
    v-on="$listeners"
    :before-close="onCloseModal"
    append-to-body
    @open="modalOpened">
    <VLoadingSpinner v-if="loading" />
    <div v-if="!confirmDD">
      <div slot="row">
        <VCheckbox
          v-if="!omitDD"
          type="radio"
          name="reminder-interval"
          v-model="type"
          :key="type"
          radio-value="card"
          :checked="type === 'card'"
          data-ec="billing_add_payment_method_modal"
          data-et="card_checkbox"
        />
        <b class="check-label">
          &ensp;<span>{{ $t('companySettings.billing.paymentMethods.paymentCard') }}</span>
        </b><br />
        <VCheckbox
          v-if="!omitDD"
          type="radio"
          name="reminder-interval"
          v-model="type"
          radio-value="direct-debit"
          :checked="type === 'direct-debit'"
          data-ec="billing_add_payment_method_modal"
          data-et="dd_checkbox"
        />
        <span v-if="!omitDD"><b class="check-label">
          &ensp;{{ $t('companySettings.billing.paymentMethods.directDebit') }}
        </b><br /></span>
        <hr />
      </div>
      <VForm id="payment-form" :onSubmit="addCard" v-show="type === 'card'">
        <div slot="row">
          <div class="form-row">
            <div class="element-container">
              <div id="card-element" ref="cardElement">
                <!-- A Stripe Element will be inserted here. -->
              </div>
            </div>
            <VMessage
              type="error"
              subtype="basic"
              v-show="stripeError">
              <template v-slot:icon>
                <XMarkIcon />
              </template>
              <template v-slot:text>
                <span role="alert">{{ stripeError }}</span>
              </template>
            </VMessage>
          </div>
        </div>
        <div slot="formButtons" class="stripe-submit-button">
          <VButton
              type="success"
              class="float-right"
              subtype="submit"
              href="#"
              data-ec="billing_add_payment_method_modal"
              data-et="add_method_button">
            <PlusIcon />
            <span>{{ $t('companySettings.billing.paymentMethods.addPaymentMethod') }}</span>
          </VButton>
        </div>
      </VForm>
      <VForm :onSubmit="validateDirectDebit" :data-vv-scope="ddScope" v-show="type === 'direct-debit'">
        <div slot="row">
          <b>{{ $t('companySettings.billing.paymentMethods.accountHolderName') }}</b>
          <VInput
            type="text"
            :class="{ 'a-input--error': errors.has('dd.name') }"
            placeholder="e.g. Jo Bloggs"
            v-validate="'required'"
            data-vv-name="name"
            data-vv-as="name"
            v-model="dd.name"
            data-ec="billing_add_payment_method_modal"
            data-et="dd_name_input"
            data-vv-scope="dd"
            data-test="input-dd-name"
            data-hj-suppress
          />
          <VMessage
            type="error"
            subtype="basic"
            v-show="errors.has('dd.name')">
            <template v-slot:icon>
              <XMarkIcon />
            </template>
            <template v-slot:text>
              {{ errors.first('dd.name') }}
            </template>
          </VMessage>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="6">
              <b>{{ $t('companySettings.billing.paymentMethods.sortCode') }}</b>
              <VInput
                type="text"
                :class="{ 'a-input--error': errors.has('dd.sort') }"
                placeholder="e.g. 12-34-56"
                v-validate="validation.sortcode"
                data-vv-name="sort"
                data-vv-as="sort code"
                v-model="dd.sort"
                data-ec="billing_add_payment_method_modal"
                data-et="dd_sort_input"
                data-vv-scope="dd"
                data-test="input-dd-sort"
                data-hj-suppress
              />
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has('dd.sort')">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first('dd.sort') }}
                </template>
              </VMessage>
            </div>
            <div slot="gridItem" type="6">
              <b>{{ $t('companySettings.billing.paymentMethods.accountNumber') }}</b>
              <VInput
                type="text"
                :class="{ 'a-input--error': errors.has('dd.account') }"
                placeholder="e.g. 12345678"
                v-validate="validation.bank_account_uk"
                data-vv-name="account"
                data-vv-as="account number"
                data-ec="billing_add_payment_method_modal"
                data-et="dd_account_input"
                v-model="dd.account"
                data-vv-scope="dd"
                data-test="input-dd-sort"
                data-hj-suppress
              />
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has('dd.account')">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first('dd.account') }}
                </template>
              </VMessage>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <b>{{ $t('companySettings.billing.paymentMethods.billingAddress') }}</b>
          <VInput
            type="text"
            :class="{ 'a-input--error': errors.has('dd.line1') }"
            placeholder="e.g. 3 Unity Street"
            v-validate="'required'"
            data-vv-name="line1"
            data-vv-as="billing address"
            v-model="dd.line1"
            data-ec="billing_add_payment_method_modal"
            data-et="dd_address_line1_input"
            data-vv-scope="dd"
            data-test="input-dd-line1"
            data-hj-suppress
          />
          <VMessage
            type="error"
            subtype="basic"
            v-show="errors.has('dd.line1')">
            <template v-slot:icon>
              <XMarkIcon />
            </template>
            <template v-slot:text>
              {{ errors.first('dd.line1') }}
            </template>
          </VMessage>
        </div>
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="6">
              <b>{{ $t('companySettings.billing.paymentMethods.city') }}</b>
              <VInput
                type="text"
                :class="{ 'a-input--error': errors.has('dd.city') }"
                placeholder="e.g. Bristol"
                v-validate="'required'"
                data-vv-name="city"
                data-vv-as="city"
                data-ec="billing_add_payment_method_modal"
                data-et="dd_address_city_input"
                v-model="dd.city"
                data-vv-scope="dd"
                data-test="input-dd-city"
                data-hj-suppress
              />
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has('dd.city')">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first('dd.city') }}
                </template>
              </VMessage>
            </div>
            <div slot="gridItem" type="6">
              <b>{{ $t('companySettings.billing.paymentMethods.postcode') }}</b>
              <VInput
                type="text"
                :class="{ 'a-input--error': errors.has('dd.postcode') }"
                placeholder="e.g. BS1 5HH"
                v-validate="'required'"
                data-vv-name="postcode"
                data-vv-as="postcode"
                v-model="dd.postcode"
                data-ec="billing_add_payment_method_modal"
                data-et="dd_address_postcode_input"
                data-vv-scope="dd"
                data-test="input-dd-postcode"
                data-hj-suppress
              />
              <VMessage
                type="error"
                subtype="basic"
                v-show="errors.has('dd.postcode')">
                <template v-slot:icon>
                  <XMarkIcon />
                </template>
                <template v-slot:text>
                  {{ errors.first('dd.postcode') }}
                </template>
              </VMessage>
            </div>
          </VGrid>
        </div>
        <div slot="row">
          <b>{{ $t('companySettings.billing.paymentMethods.country') }}</b>
          <select v-model="dd.country">
            <option
                v-for="country in countryCodes"
                :value="country"
                :key="country.id"
                data-ec="billing_add_payment_method_modal"
                data-et="country_dropdown">
              {{ country.title }}
            </option>
          </select>
          <!-- <VDropdown
            :list="countryCodes"
            :fluid="true"
            v-model="dd.country"
            data-test="dropdown-dd-country"
            :selected="{ id: dd.country.id }"
            data-hj-suppress></VDropdown> -->
        </div>
        <div slot="row">
          <p>
            Domo Group Ltd. 3 Unity Street, Bristol, BS1 5HH<br />
            0800 612 6263 | help@signable.co.uk
          </p>
          <p>Direct Debits are covered by the <a href="https://stripe.com/bacs_debit/direct-debit-guarantee" target="_blank">Direct Debit Guarantee</a>.</p>
          <p>You will receive 2 days advance notice via email of any amount to be debited under this instruction. Any payments will be shown with the statement descriptor of 'SIGNABLE'.</p>
        </div>
        <div slot="formButtons" type="right">
          <VButton
            type="cta-1"
            subtype="submit"
            href="#"
            data-test="dd-button-next"
            data-ec="billing_add_payment_method_modal"
            data-et="next_button">
            <ArrowRightIcon />
            <span>{{ $t('functional.next') }}</span>
          </VButton>
        </div>
      </VForm>
    </div>
    <div class="modal-add-payment__confirm-dd" v-else>
      <div v-if="!DDConfirmComplete">
        <p>
          {{ $t('companySettings.billing.paymentMethods.directDebitConfirmMessage') }}
        </p>
        <hr />
        <div>
          <p>
            <b>{{ $t('companySettings.billing.paymentMethods.accountHolderName') }}</b><br />
            {{ dd.name }}
          </p>
          <p>
            <b>{{ $t('companySettings.billing.paymentMethods.sortCode') }}</b><br />
            {{ dd.sort }}
          </p>
          <p>
            <b>{{ $t('companySettings.billing.paymentMethods.accountNumber') }}</b><br />
            {{ dd.account }}
          </p>
          <p>
            <b>{{ $t('companySettings.billing.paymentMethods.billingAddress') }}</b><br />
            {{ dd.line1 }} <br />
            <span v-if="dd.line2">{{ dd.line2 }} <br /></span>
            {{ dd.city }}<br />
            {{ dd.postcode }}<br />
            {{ dd.country.title }}
          </p>
        </div>
        <div class="modal-add-payment__confirm-dd__checkbox">
          <p>
            <VCheckbox type="checkbox"
              v-model="authCheck"
              :checked="authCheck"
            />
            <span>{{ $t('companySettings.billing.paymentMethods.confirmAuthorised') }}</span>
          </p>
        </div>
        <hr />
        <div class="modal-add-payment__confirm-dd__buttons">
          <VButton
            type="success"
            :action="addDirectDebit"
            data-test="dd-button-next"
            :disabled="!authCheck"
            data-ec="billing_add_payment_method_modal"
            data-et="dd_next_button">
            <CheckIcon />
            <span>{{ $t('companySettings.billing.paymentMethods.addDirectDebit') }}</span>
          </VButton>
          <VButton
            type="outline-1"
            :action="directDebitBack"
            data-test="dd-button-next"
            data-ec="billing_add_payment_method_modal"
            data-et="dd_back_button">
            <ArrowLeftIcon />
            <span>{{ $t('functional.back') }}</span>
          </VButton>
        </div>
      </div>
      <div v-else>
        <p class="pre-wrap">{{ $t('companySettings.billing.paymentMethods.directDebitConfirmedMessage') }}</p>
        <hr />
        <div class="modal-add-payment__confirm-dd__buttons">
          <VButton
            type="outline-1"
            :action="onCloseModal"
            data-test="dd-button-close"
            data-ec="billing_add_payment_method_modal"
            data-et="dd_ok_button">
            <CheckIcon />
            <span>{{ $t('functional.okay') }}</span>
          </VButton>
        </div>
      </div>
    </div>
  </ElDialog>
</template>

<script>
import countryCodeJSON from '@/assets/iso3166-1a2.json';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import validation from '@/utils/validation';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, PlusIcon, XMarkIcon } from '@/components/icons/fontIcons';

import { mapGetters, mapState } from 'vuex';

export default {
  name: 'AddPaymentMethodModal',
  inject: {
    $validator: '$validator',
  },
  components: {
    VButton,
    VCheckbox,
    VInput,
    VMessage,
    VForm,
    VGrid,
    VLoadingSpinner,
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckIcon,
    PlusIcon,
    XMarkIcon,
  },
  props: {
    show: Boolean,
    ddScope: String,
  },
  data() {
    return {
      validation,
      type: 'card',
      stripe: null,
      stripeError: '',
      card: {},
      dd: {
        name: '',
        sort: null,
        account: null,
        line1: '',
        city: '',
        postcode: '',
        country: {
          id: 234,
          title: 'United Kingdom of Great Britain and Northern Ireland',
          code: 'GB',
        },
      },
      confirmDD: false,
      DDConfirmComplete: false,
      loading: false,
      authCheck: false,
      omitDD: false,
      paymentComplete: false,
    };
  },
  watch: {
    show() {
      if (this.type === 'card') {
        this.$nextTick(() => {
          this.stripe = Stripe(process.env.VUE_APP_STRIPE_TOKEN); // eslint-disable-line
          const elements = this.stripe.elements();

          this.card = elements.create('card'); // Create an instance of the card Element.
          this.card.mount(this.$refs.cardElement); // Add an instance of the card Element into the `card-element`.

          this.card.addEventListener('change', ({ error }) => {
            if (error) {
              this.stripeError = error.message;
            } else {
              this.stripeError = '';
            }
          });
        });
      }
    },
  },
  computed: {
    ...mapState({
      companyData: state => state.company.company,
    }),
    ...mapGetters({
      currentPlan: 'plans/current',
    }),
    countryCodes() {
      return countryCodeJSON;
    },
    title() {
      return this.paymentComplete ? this.$t('companySettings.billing.paymentMethods.paymentMethodComplete') : this.$t('companySettings.billing.paymentMethods.addPaymentMethod');
    },
  },
  methods: {
    modalOpened() {
      this.paymentComplete = false;
      this.loading = true;
      this.errors.clear();
      this.stripeError = '';
      this.checkPermissionToDisplayDD();
    },
    checkPermissionToDisplayDD() {
      this.$store.dispatch('company/GET').finally(() => {
        if (this.companyData.country_code !== 'GB' || this.currentPlan.id === 'pay-as-you-go') {
          this.omitDD = true;
        }
        this.loading = false;
      });
    },
    onCloseModal() {
      this.errors.clear();
      this.confirmDD = false;
      this.DDConfirmComplete = false;
      this.dd = {
        name: '',
        sort: null,
        account: null,
        line1: '',
        city: '',
        postcode: '',
        country: {
          id: 234,
          title: 'United Kingdom of Great Britain and Northern Ireland',
          code: 'GB',
        },
      };

      if (this.card && this.card.hasOwnProperty('clear')) this.card.clear(); // eslint-disable-line no-prototype-builtins
      this.type = 'card';
      this.authCheck = false;
      this.$emit('close');
    },
    validateDirectDebit(scope) {
      this.$validator.validateAll(scope).then((validated) => {
        if (validated) {
          this.confirmDD = true;
        }
      });
    },
    async addDirectDebit() {
      this.loading = true;
      const country = this.countryCodes.find(c => c.id === this.dd.country.id);

      const details = {
        account_name: this.dd.name,
        sort_code: this.dd.sort,
        account_number: this.dd.account,
        billing_address: {
          line1: this.dd.line1,
          city: this.dd.city,
          country: country.code,
          postal_code: this.dd.postcode,
        },
      };

      const { createError } = await this.$store.dispatch('paymentMethods/CREATE_DD', details);

      if (createError) {
        this.onCloseModal();
      } else {
        this.DDConfirmComplete = true;
      }

      this.loading = false;
      this.paymentComplete = true;
    },
    async addCard() {
      this.loading = true;

      const { clientSecret } = await this.$store.dispatch('paymentMethods/GET_CLIENT_SECRET');
      const { setupIntent, error } = await this.stripe.handleCardSetup(clientSecret, this.card);

      if (error) {
        this.loading = false;
        this.$bus.$emit('renderGlobalMessage', ({
          message: error.message,
          icon: 'times',
          type: 'error',
        }));
        throw error;
      } else {
        this.$store.dispatch('paymentMethods/CREATE_CARD', { paymentMethodId: setupIntent.payment_method }).then(() => {
          this.loading = false;
          this.onCloseModal();
        });
      }

      return true;
    },
    directDebitBack() {
      this.confirmDD = false;
    },
  },
};
</script>

<style scoped>
.card-icons {
  margin-left: var(--spacing-xs);
}

.card-icons > svg {
  width: auto;
  height: 24px;
  vertical-align: middle;
  margin-bottom: var(--spacing-xs);
  margin-left: var(--spacing-xs);
}

.StripeElement,
select {
  padding: 8px 0px 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  transition: border .25s ease, box-shadow .25s ease, color .25s ease;
  font-family: "Roboto", sans-serif !important;
  background: white;
  margin-bottom: var(--spacing-base);
}

.StripeElement--focus,
select:active {
  border-color: var(--signable-secondary-2-shade-3);
  box-shadow: 0px 0px 0px 2px rgba(141, 173, 192, 0.25);
}

select {
  width: 100%;
  height: 38px;
  font-size: 16px;
}

.stripe-submit-button button {
  margin: var(--spacing-base) 0;
}

.pre-wrap{
  white-space: pre-wrap;
}

.modal-add-payment__confirm-dd__buttons {
  display: block;
  height: 38px;
}
.modal-add-payment__confirm-dd__buttons .a-button {
  float: right;
  margin-left: var(--spacing-small);
}
.modal-add-payment__confirm-dd__checkbox {
  margin-top: var(--spacing-base);
}
.modal-add-payment__confirm-dd__checkbox * {
  vertical-align: middle;
}
.modal-add-payment__confirm-dd__checkbox .a-checkbox {
  margin-right: 10px;
}
</style>
